import { offersApi } from "@services";
import { IOffer, IRegion, OfferModellerAddedField, Stage } from "@types";
import { createSlice, current, isAnyOf, isPending } from "@reduxjs/toolkit";
import {
  getTotal,
  calcluateFixedTotal,
  calcluateStiTotal,
  round,
  getTotalForBeneFit,
  calculatePercentageAmount,
  getTotalLti,
} from "@/utils";
import { message } from "antd";
import customErrorMessage from "@/components/CustomErrorMessage";

type NullOrString = null | string;
type NullOrNumber = null | number;

type NameAndID = null | {
  name: string;
  id: number;
};

interface IPositionDetails {
  grade: NullOrString;
  job_title: NullOrString;
  type: NullOrString;
  business_unit: NameAndID;
  region: Partial<IRegion> | null;
  country_id: NullOrNumber;
  company_job_function: NameAndID;
  company_sub_job_function_id: NullOrNumber;
  reporting_grade: NullOrString;
  position_owner: NullOrString;
  city: NullOrString;
  grade_type: NullOrString;
  reporting_title: NullOrString;
  cost_centre: NullOrString;
  is_critical: boolean;
  currency_id: NullOrNumber;
  position_comment: NullOrString;
}

interface ICandidateDetails {
  candidate_name: NullOrString;
  company_name: NullOrString;
  current_title: NullOrString;
  current_grade: NullOrString;
  business_unit: NullOrString;
  region: NullOrString;
  location: NullOrString;
  relevant_experience: NullOrNumber;
  total_experience: NullOrNumber;
  is_critical_skills: boolean;
  critical_skills: NullOrString;
  is_referral: boolean;
  referral_employees: NullOrString;
  country_id: NullOrNumber;
  currency_id: NullOrNumber;
  current_job_function: NameAndID | null;
  current_sub_job_function_id: NullOrNumber;
  sector: NameAndID | null;
  industry: NameAndID | null;
  sub_industry_id: NullOrNumber;
  email?: string;
  candidate_email?: string;
  // gender: NullOrString;
}

interface IOfferFixedCash {
  current_annual_base: NullOrNumber;
  proposed_annual_base: NullOrNumber;
  current_compa_ratio: NullOrNumber;
  proposed_compa_ratio: NullOrNumber;
  current_market_ratio: NullOrNumber;
  proposed_market_ratio: NullOrNumber;
  current_total: NullOrNumber;
  proposed_total: NullOrNumber;
  allowances: OfferModellerAddedField[];
}

interface IOfferSti {
  current_bonus_target: NullOrNumber;
  proposed_bonus_target: NullOrNumber;
  current_sale_incentive: NullOrNumber;
  proposed_base_pay_percentage: NullOrNumber;
  is_sale_incentive: boolean;
  is_current_sale_incentive: boolean;
  // is_sale_temp: boolean; //comment by areeb
  current_total_bonus: NullOrNumber;
  proposed_total_bonus: NullOrNumber;
  bonuses: OfferModellerAddedField[];
  // new fields for target cash calculation
  current_total_target_cash: NullOrNumber;
  proposed_total_target_cash: NullOrNumber;
}

interface IOfferLti {
  current_unvested_equity: NullOrNumber;
  proposed_new_hire_equity: NullOrNumber;
  current_annual_grant: NullOrNumber;
  proposed_annual_grant: NullOrNumber;
  current_total_lti: NullOrNumber;
  proposed_total_lti: NullOrNumber;
  other_ltis: OfferModellerAddedField[];
  selected_stock_value?: number;
  // new fields for target cash calculation
  current_total_direct_comp: NullOrNumber;
  proposed_total_direct_comp: NullOrNumber;
}

interface IOfferSignOnBonus {
  current_total_bonus: NullOrNumber;
  proposed_total_bonus: NullOrNumber;
  bonuses: OfferModellerAddedField[];
}

interface IOfferBenefits {
  current_total_bonus: NullOrNumber;
  proposed_total_bonus: NullOrNumber;
  bonuses: OfferModellerAddedField[];
  // new fields for target cash calculation
  current_total_remuneration: NullOrNumber;
  proposed_total_remuneration: NullOrNumber;
}

interface IOfferModeller {
  offer_fixed_cash: IOfferFixedCash;
  offer_sti: IOfferSti;
  offer_lti: IOfferLti;
  offer_sign_on_bonus: IOfferSignOnBonus;
  offer_benefits: IOfferBenefits;
}

type OfferModellerKeys = keyof IOfferModeller;

interface IInitialState {
  currencyRate: NullOrNumber;
  currencyCode: NullOrNumber;
  currentCandidateCurrency: NullOrNumber;
  offerCurrency: NullOrNumber;
  stiBonusCurrencyRate: NullOrNumber;
  currencyName: string;
  positionDetails: IPositionDetails;
  candidateDetails: ICandidateDetails;
  offerModeller: IOfferModeller;
  hidden_sti_bonuses: OfferModellerAddedField[];
  offer: Partial<IOffer> | null;
  stock_data: null;
  offer_annual: boolean;
  show_current: boolean;
  shouldAutoPopulate: boolean;
  allowance_data_fingerprint: string | null;
}

function object_equals(x: any, y: any) {
  if (x === y) return true;
  // if both x and y are null or undefined and exactly the same

  if (!(x instanceof Object) || !(y instanceof Object)) return false;
  // if they are not strictly equal, they both need to be Objects

  if (x.constructor !== y.constructor) return false;
  // they must have the exact same prototype chain, the closest we can do is
  // test there constructor.

  for (var p in x) {
    if (!x.hasOwnProperty(p)) continue;
    // other properties were tested using x.constructor === y.constructor

    if (!y.hasOwnProperty(p)) return false;
    // allows to compare x[ p ] and y[ p ] when set to undefined

    if (x[p] === y[p]) continue;
    // if they have the same strict value or identity then they are equal

    if (typeof x[p] !== "object") return false;
    // Numbers, Strings, Functions, Booleans must be strictly equal

    if (!object_equals(x[p], y[p])) return false;
    // Objects and Arrays must be tested recursively
  }

  for (p in y) if (y.hasOwnProperty(p) && !x.hasOwnProperty(p)) return false;
  // allows x[ p ] to be set to undefined

  return true;
}

export const offerModellerInitialState = {
  offer_fixed_cash: {
    current_annual_base: null,
    proposed_annual_base: null,
    current_compa_ratio: null,
    proposed_compa_ratio: null,
    current_market_ratio: null,
    proposed_market_ratio: null,
    current_total: null,
    proposed_total: null,
    allowances: [],
  },
  offer_sti: {
    current_bonus_target: null,
    proposed_bonus_target: null,
    current_sale_incentive: null,
    proposed_base_pay_percentage: null,
    is_sale_incentive: false,
    is_current_sale_incentive: false,
    // is_sale_temp: false,//comment by areeb
    current_total_bonus: null,
    proposed_total_bonus: null,
    bonuses: [],
    // add new field
    current_actual_sales_incentive: null,
    current_actual_bonus_amount: null,
    // new fields for target cash calculation
    current_total_target_cash: null,
    proposed_total_target_cash: null,
  },
  offer_lti: {
    current_unvested_equity: null,
    proposed_new_hire_equity: null,
    current_annual_grant: null,
    proposed_annual_grant: null,
    current_total_lti: null,
    proposed_total_lti: null,
    other_ltis: [],
    // new fields for target cash calculation
    current_total_direct_comp: null,
    proposed_total_direct_comp: null,
  },
  offer_sign_on_bonus: {
    current_total_bonus: null,
    proposed_total_bonus: null,
    bonuses: [],
  },
  offer_benefits: {
    current_total_bonus: null,
    proposed_total_bonus: null,
    bonuses: [],
    // new fields for target cash calculation
    current_total_remuneration: null,
    proposed_total_remuneration: null,
  },
};

const offerModellerState = offerModellerInitialState;

const initialState: IInitialState = {
  currencyRate: null,
  currencyName: "",
  stiBonusCurrencyRate: null,
  offerCurrency: null,
  hidden_sti_bonuses: [],
  currencyCode: null,
  positionDetails: {
    grade: null,
    job_title: null,
    currency_id: null,
    type: null,
    region: null,
    country_id: null,
    company_job_function: null,
    company_sub_job_function_id: null,
    reporting_grade: null,
    position_owner: null,
    city: null,
    grade_type: null,
    reporting_title: null,
    cost_centre: null,
    is_critical: true,
    position_comment: null,
    business_unit: null,
  },
  candidateDetails: {
    candidate_name: null,
    company_name: null,
    current_title: null,
    current_grade: null,
    business_unit: null,
    region: null,
    location: null,
    relevant_experience: null,
    total_experience: null,
    is_critical_skills: false,
    critical_skills: null,
    is_referral: false,
    referral_employees: null,
    country_id: null,
    currency_id: null,
    current_job_function: null,
    current_sub_job_function_id: null,
    sector: null,
    industry: null,
    sub_industry_id: null,
    // gender: null,
  },
  offerModeller: offerModellerState,
  offer: null,
  stock_data: null,
  offer_annual: true,
  show_current: true,
  shouldAutoPopulate: false,
  allowance_data_fingerprint: null,
};

/* ------------------------------------------------------------------
 * Keeping the user's own edits through a "Fetch Latest Data"
 *
 * preserveManualAndCurrent() below keeps hand-added and current-side rows.
 * These helpers cover the other half: rows that ARE auto-populated but whose
 * numbers the user has since overridden.
 *
 * Rows are matched by name + is_current, NOT by id: rows from the
 * auto-populate endpoint carry the company reference id (cash allowance id,
 * plan id) while rows loaded from a saved offer carry the offer row id.
 * name + is_current is what the backend itself keys rows on when saving.
 * ------------------------------------------------------------------ */
const rowKey = (row: any) =>
  `${row?.is_current ? "cur" : "prop"}::${String(row?.name ?? "")
    .trim()
    .toLowerCase()}`;

// Fields the user types into. `value` is deliberately ignored on percentage
// rows because it is derived from the percentage and gets recalculated every
// time proposed_annual_base changes - comparing it would flag every row.
const USER_INPUT_FIELDS: Record<string, string[]> = {
  offer_fixed_cash: [
    "name",
    "value",
    "original_value",
    "percentage",
    "is_percentage",
  ],
  offer_sti: ["name", "value", "original_value", "percentage", "is_percentage"],
  offer_lti: ["name", "value", "equity_mid", "unit"],
  offer_benefits: [
    "value",
    "original_value",
    "percentage",
    "custom_detail",
    "exclude_from_offer",
  ],
};

// Values restored from the user's row when fresh allowance data arrives.
const PRESERVED_FIELDS: Record<string, string[]> = {
  offer_fixed_cash: ["value", "original_value", "percentage", "is_percentage"],
  offer_sti: ["value", "original_value", "percentage", "is_percentage"],
  offer_lti: ["value", "equity_mid", "unit"],
  offer_benefits: [
    "value",
    "original_value",
    "percentage",
    "custom_detail",
    "show_custom_detail",
    "exclude_from_offer",
  ],
};

const ROW_LIST_KEY: Record<string, string> = {
  offer_fixed_cash: "allowances",
  offer_sti: "bonuses",
  offer_lti: "other_ltis",
  offer_benefits: "bonuses",
};

/**
 * Flags rows whose user-editable fields just changed. The flag is persisted by
 * the backend (is_user_modified) so it survives save + reload.
 */
const markUserEdits = (prevRows: any[], nextRows: any[], section: string) => {
  const fields = USER_INPUT_FIELDS[section];
  if (!fields || !Array.isArray(nextRows)) return nextRows;

  const prevByKey = new Map(
    (prevRows || []).map((row: any) => [rowKey(row), row])
  );

  return nextRows.map((row: any) => {
    if (row?.is_user_modified) return row;

    const prev: any = prevByKey.get(rowKey(row));
    if (!prev) return row; // brand new row, nothing to compare against
    if (prev.is_user_modified) return { ...row, is_user_modified: true };

    const compared = row?.is_percentage
      ? fields.filter(field => field !== "value")
      : fields;
    const edited = compared.some(
      field => (prev[field] ?? null) !== (row[field] ?? null)
    );

    return edited ? { ...row, is_user_modified: true } : row;
  });
};

/**
 * Puts the user's own numbers back onto freshly fetched auto-populate rows.
 * Fresh metadata (name, limits, calculation basis...) is still taken from the
 * API - only the values the user typed are restored, and only on rows they
 * actually edited.
 */
const restoreUserEdits = (
  previousRows: any[],
  freshRows: any[],
  section: string
) => {
  const preserved = PRESERVED_FIELDS[section] || [];
  const previousByKey = new Map(
    (previousRows || [])
      .filter((row: any) => row?.is_user_modified)
      .map((row: any) => [rowKey(row), row])
  );

  if (!previousByKey.size) return freshRows || [];

  return (freshRows || []).map((row: any) => {
    const previous: any = previousByKey.get(rowKey(row));
    if (!previous) return row;

    const keptValues: any = {};
    preserved.forEach(field => {
      if (previous[field] !== undefined) keptValues[field] = previous[field];
    });

    return { ...row, ...keptValues, is_user_modified: true };
  });
};

const offersSlice = createSlice({
  name: "offers",
  initialState,
  reducers: {
    handlePositionDetails: (state, action) => {
      const previousGrade = state.positionDetails.grade;
      const newGrade = action.payload.grade;

      state.positionDetails = { ...state.positionDetails, ...action.payload };

      // trigger autopopulate only if grade changed
      if (newGrade && newGrade !== previousGrade) {
        state.shouldAutoPopulate = true;
      }
    },
    handleCandidateDetails: (state, action) => {
      state.candidateDetails = { ...state.candidateDetails, ...action.payload };
    },
    handleStage: (state, action: { payload: Stage }) => {
      state.offer = { ...state?.offer, stage: action.payload };
    },
    handleCurrency: (state, action) => {
      state.currencyCode = action.payload.id;
      state.currencyName = action.payload.code;
    },
    removeCurrency: state => {
      state.currencyCode = null;
      state.currencyRate = null;
      state.currencyName = "";
      state.stiBonusCurrencyRate = null;
    },

    handleHiddenStiBonuses: (state, action) => {
      state.hidden_sti_bonuses = action.payload;
    },
    handleOfferModeller: (
      state,
      action: {
        payload: {
          key: OfferModellerKeys;
          data: Partial<
            | IOfferFixedCash
            | IOfferSti
            | IOfferLti
            | IOfferBenefits
            | IOfferSignOnBonus
          >;
        };
      }
    ) => {
      const sectionKey = action.payload.key;
      const listKey = ROW_LIST_KEY[sectionKey];
      const incomingRows = listKey
        ? (action.payload.data as any)?.[listKey]
        : undefined;

      // Remember which rows the user touched so that "Fetch Latest Data"
      // (and the grade-change auto-populate) will not overwrite them.
      const payloadData = Array.isArray(incomingRows)
        ? {
            ...action.payload.data,
            [listKey]: markUserEdits(
              (state.offerModeller as any)[sectionKey]?.[listKey],
              incomingRows,
              sectionKey
            ),
          }
        : action.payload.data;

      state.offerModeller = {
        ...state.offerModeller,
        [sectionKey]: {
          ...state.offerModeller[sectionKey],
          ...payloadData,
        },
      };
      if (action.payload.key === "offer_fixed_cash") {
        const updatedBonuses =
          state.offerModeller.offer_fixed_cash?.allowances?.map(bonus => {
            const { is_percentage, is_basic_pay, original_value, value } =
              bonus;

            let calculatedValue;

            if (is_percentage) {
              calculatedValue = is_basic_pay
                ? calculatePercentageAmount(
                    Number(
                      state.offerModeller.offer_fixed_cash.proposed_annual_base
                    ),
                    Number(original_value)
                  )
                : calculatePercentageAmount(
                    Number(
                      state.offerModeller.offer_fixed_cash.proposed_annual_base
                    ),
                    Number(original_value)
                  );
            } else {
              calculatedValue = value;
            }

            return {
              ...bonus,
              value: calculatedValue,
            };
          });
        const { current_total, proposed_total } = calcluateFixedTotal(
          state.offerModeller.offer_fixed_cash,
          [
            "current_compa_ratio",
            "current_market_ratio",
            "proposed_compa_ratio",
            "proposed_market_ratio",
          ]
        );
        state.offerModeller.offer_fixed_cash.current_total = current_total;
        state.offerModeller.offer_fixed_cash.proposed_total = proposed_total;
        state.offerModeller.offer_fixed_cash.allowances = updatedBonuses;
      } else if (action.payload.key === "offer_sti") {
        const updatedBonuses = state.offerModeller.offer_sti?.bonuses.map(
          bonus => {
            const { is_percentage, is_basic_pay, original_value, value } =
              bonus;

            let calculatedValue;

            if (is_percentage) {
              calculatedValue = is_basic_pay
                ? calculatePercentageAmount(
                    Number(
                      state.offerModeller.offer_fixed_cash.proposed_annual_base
                    ),
                    Number(original_value)
                  )
                : calculatePercentageAmount(
                    Number(state.offerModeller.offer_fixed_cash.proposed_total),
                    Number(original_value)
                  );
            } else {
              calculatedValue = value;
            }

            return {
              ...bonus,
              value: calculatedValue,
            };
          }
        );
        const { current_total, proposed_total } = calcluateStiTotal(
          {
            ...state.offerModeller.offer_sti,
            bonuses: updatedBonuses,
            current_bonus_target:
              state.offerModeller.offer_fixed_cash?.current_annual_base &&
              state.offerModeller.offer_sti?.current_bonus_target &&
              !state.offerModeller?.offer_sti.is_current_sale_incentive
                ? round(
                    (state.offerModeller.offer_fixed_cash?.current_annual_base *
                      state.offerModeller.offer_sti?.current_bonus_target) /
                      100
                  )
                : 0,

            proposed_bonus_target:
              state.offerModeller.offer_fixed_cash?.proposed_annual_base &&
              state.offerModeller.offer_sti?.proposed_bonus_target
                ? round(
                    (state.offerModeller.offer_fixed_cash
                      ?.proposed_annual_base *
                      state.offerModeller.offer_sti?.proposed_bonus_target) /
                      100
                  )
                : null,
            fixed_proposed_total:
              state.offerModeller.offer_fixed_cash.proposed_total,
            proposed_annual_base:
              state.offerModeller.offer_fixed_cash.proposed_annual_base,
            // bonuses: updatedBonuses,
          },
          []
        );

        state.offerModeller.offer_sti.current_total_bonus = current_total;
        state.offerModeller.offer_sti.proposed_total_bonus = proposed_total;
        state.offerModeller.offer_sti.bonuses = updatedBonuses;

        //
        const current_total_target_cash =
          Number(state.offerModeller.offer_fixed_cash.current_total) +
          Number(state.offerModeller.offer_sti?.current_total_bonus);
        const proposed_total_target_cash =
          state.offerModeller.offer_sti?.is_sale_incentive &&
          state.offerModeller.offer_fixed_cash?.proposed_annual_base &&
          state.offerModeller.offer_sti.proposed_base_pay_percentage
            ? (state.offerModeller.offer_fixed_cash.proposed_annual_base *
                ((state.offerModeller.offer_fixed_cash.proposed_annual_base *
                  (100 -
                    state.offerModeller.offer_sti
                      .proposed_base_pay_percentage)) /
                  100)) /
                ((state.offerModeller.offer_fixed_cash.proposed_annual_base *
                  state.offerModeller.offer_sti.proposed_base_pay_percentage) /
                  100) -
              state.offerModeller.offer_sti.proposed_base_pay_percentage +
              ((state.offerModeller.offer_sti?.proposed_total_bonus || 0) +
                (state.offerModeller.offer_fixed_cash?.proposed_total || 0))
            : (state.offerModeller.offer_sti?.proposed_total_bonus || 0) +
              (state.offerModeller.offer_fixed_cash?.proposed_total || 0);
        state.offerModeller.offer_sti.current_total_target_cash =
          current_total_target_cash;
        state.offerModeller.offer_sti.proposed_total_target_cash =
          proposed_total_target_cash;
      } else if (action.payload.key === "offer_lti") {
        const totalDirectLTICurrent = (currentTotal: number) => {
          const result =
            Number(state.offerModeller.offer_fixed_cash?.current_total) +
            Number(state.offerModeller.offer_sti?.current_total_bonus);
          return result + Number(currentTotal);
        };
        const totalDirectLTIProposed = (proposedTotal: number) => {
          const result =
            state.offerModeller.offer_sti?.is_sale_incentive &&
            state.offerModeller.offer_fixed_cash?.proposed_annual_base &&
            state.offerModeller.offer_sti.proposed_base_pay_percentage
              ? round(
                  (state.offerModeller.offer_fixed_cash.proposed_annual_base *
                    ((state.offerModeller.offer_fixed_cash
                      .proposed_annual_base *
                      (100 -
                        state.offerModeller.offer_sti
                          .proposed_base_pay_percentage)) /
                      100)) /
                    ((state.offerModeller.offer_fixed_cash
                      .proposed_annual_base *
                      state.offerModeller.offer_sti
                        .proposed_base_pay_percentage) /
                      100) -
                    state.offerModeller.offer_sti.proposed_base_pay_percentage
                ) +
                ((state.offerModeller.offer_sti?.proposed_total_bonus || 0) +
                  (state.offerModeller.offer_fixed_cash?.proposed_total || 0))
              : (state.offerModeller.offer_sti?.proposed_total_bonus || 0) +
                (state.offerModeller.offer_fixed_cash?.proposed_total || 0);
          return result + proposedTotal;
        };
        const { current_total, proposed_total } = getTotalLti(
          state.offerModeller.offer_lti
        );
        const current_total_direct_comp = totalDirectLTICurrent(current_total);
        const totalLtis = state.offerModeller.offer_lti?.proposed_total_lti;
        const proposed_total_direct_comp = totalDirectLTIProposed(
          totalLtis || 0
        );

        state.offerModeller.offer_lti.current_total_lti = current_total;
        state.offerModeller.offer_lti.proposed_total_lti = proposed_total;
        // new fields for target cash calculation
        state.offerModeller.offer_lti.current_total_direct_comp =
          current_total_direct_comp;
        state.offerModeller.offer_lti.proposed_total_direct_comp =
          proposed_total_direct_comp;
      } else if (action.payload.key === "offer_benefits") {
        const proposedLtiStockAware = (() => {
          const base =
            Number(state.offerModeller.offer_lti?.proposed_total_lti) || 0;
          const stock =
            Number(
              (state.offerModeller.offer_lti as any)?.selected_stock_value
            ) || 1;

          // If other_ltis exist, replace any "unit === false" entries with amount = value * stock
          const adjusted = (state.offerModeller.offer_lti?.other_ltis || [])
            .filter((x: any) => !x?.is_current)
            .reduce((sum: number, it: any) => {
              const value = Number(it?.value) || 0;
              if (!it?.unit && value) {
                // proposed_total_lti likely included raw value; replace raw with stock*value
                return sum - value + round(value * (stock || 1));
              }
              return sum;
            }, base);

          return round(adjusted || 0);
        })();
        const proposedBenefitsTotal = (
          state.offerModeller.offer_benefits?.bonuses || []
        )
          .filter(b => !b?.is_current)
          .reduce((sum, b) => {
            const value = Number(b?.value) || 0;
            const basis = b?.calculation_basis;
            // percent_basic → calculate percentage
            if (basis === "percent_basic") {
              return (
                sum +
                calculatePercentageAmount(
                  Number(
                    state.offerModeller.offer_fixed_cash
                      ?.proposed_annual_base || 0
                  ),
                  value
                )
              );
            } else if (basis === "percent_guaranteed") {
              return (
                sum +
                calculatePercentageAmount(
                  Number(
                    state.offerModeller.offer_fixed_cash?.proposed_total || 0
                  ),
                  value
                )
              );
            }

            // default → flat value
            return sum + value;
          }, 0);
        let totalBenefitProposed = (proposedBenefits: number) => {
          const result =
            state.offerModeller.offer_sti?.is_sale_incentive &&
            state.offerModeller.offer_fixed_cash?.proposed_annual_base &&
            state.offerModeller.offer_sti?.proposed_base_pay_percentage
              ? round(
                  (state.offerModeller.offer_fixed_cash.proposed_annual_base *
                    ((state.offerModeller.offer_fixed_cash
                      .proposed_annual_base *
                      (100 -
                        state.offerModeller.offer_sti
                          .proposed_base_pay_percentage)) /
                      100)) /
                    ((state.offerModeller.offer_fixed_cash
                      .proposed_annual_base *
                      state.offerModeller.offer_sti
                        .proposed_base_pay_percentage) /
                      100) -
                    state.offerModeller.offer_sti.proposed_base_pay_percentage
                ) +
                ((state.offerModeller.offer_sti?.proposed_total_bonus || 0) +
                  (state.offerModeller.offer_fixed_cash?.proposed_total || 0))
              : (state.offerModeller.offer_sti?.proposed_total_bonus || 0) +
                (state.offerModeller.offer_fixed_cash?.proposed_total || 0);

          // ⬇️ Use stock-aware LTI here
          return result + proposedBenefits + Number(proposedLtiStockAware) || 0;
        };
        const totalBenefitCurrent = (currentTotal: number) => {
          const result =
            Number(state.offerModeller.offer_fixed_cash?.current_total) +
            Number(state.offerModeller.offer_sti?.current_total_bonus);
          return (
            result +
              currentTotal +
              Number(state.offerModeller.offer_lti?.current_total_lti) || 0
          );
        };
        const { current_total, proposed_total } = getTotalForBeneFit({
          ...state.offerModeller.offer_benefits,
          // benefits: updatedBenefits,// original_value comment
          fixed_proposed_total:
            state.offerModeller.offer_fixed_cash.proposed_total,
          proposed_annual_base:
            state.offerModeller.offer_fixed_cash.proposed_annual_base,
        });
        const current_total_bonus = state.offerModeller.offer_benefits?.bonuses
          ?.filter(({ is_current }: any) => is_current)
          .reduce((sum, item: any) => {
            return sum + item?.value;
          }, 0);
        state.offerModeller.offer_benefits.current_total_bonus =
          current_total_bonus;
        state.offerModeller.offer_benefits.proposed_total_bonus =
          proposed_total;

        //
        const current_total_remuneration = totalBenefitCurrent(
          state.offerModeller.offer_benefits?.current_total_bonus
        );

        const proposed_total_remuneration = totalBenefitProposed(
          proposedBenefitsTotal
        ) as number;

        state.offerModeller.offer_benefits.current_total_remuneration =
          current_total_remuneration;
        state.offerModeller.offer_benefits.proposed_total_remuneration =
          proposed_total_remuneration;
      } else if (action.payload.key === "offer_sign_on_bonus") {
        const { current_total, proposed_total } = getTotal(
          state.offerModeller.offer_sign_on_bonus
        );
        state.offerModeller.offer_sign_on_bonus.current_total_bonus =
          current_total;
        state.offerModeller.offer_sign_on_bonus.proposed_total_bonus =
          proposed_total;
      }
    },
    resetOffers: () => initialState,

    resetOfferModeller: (state, action) => {
      state.offerModeller = {
        ...offerModellerInitialState,
      };
    },
    convertOffer: state => {
      state.offer_annual = !state.offer_annual;
    },
    toggleShowCurrent: state => {
      state.show_current = !state.show_current;
    },
  },
  extraReducers: builder => {
    builder
      .addMatcher(
        isAnyOf(
          offersApi.endpoints.createOfferDetails.matchFulfilled,
          offersApi.endpoints.createCandidateDetails.matchFulfilled,
          offersApi.endpoints.updateOfferDetails.matchFulfilled
        ),
        (state, { payload }) => {
          state.offer = payload?.data;
        }
      )
      .addMatcher(
        offersApi.endpoints.fetchOfferById.matchFulfilled,
        (state, { payload }) => {
          const {
            offer_position_detail,
            offer_candidate_detail,
            offer_lti,
            offer_fixed_cash,
            offer_sti,
            offer_benefits,
            offer_sign_on_bonus,
          } = payload?.data;

          state.offerModeller = {
            ...state.offerModeller,
            offer_lti: offer_lti || offerModellerState["offer_lti"],
            offer_fixed_cash:
              offer_fixed_cash || offerModellerState["offer_fixed_cash"],
            offer_sti: offer_sti || offerModellerState["offer_sti"],
            offer_sign_on_bonus:
              offer_sign_on_bonus || offerModellerState["offer_sign_on_bonus"],
            offer_benefits:
              offer_benefits || offerModellerState["offer_benefits"],
          };
          const positionDetails = { ...offer_position_detail };
          const candidateDetails = { ...offer_candidate_detail };

          delete positionDetails["company_sub_job_function"];
          delete positionDetails["country"];
          delete positionDetails["currency"];
          delete positionDetails["region"];
          delete candidateDetails["country"];
          delete candidateDetails["currency"];
          delete candidateDetails["current_sub_job_function"];
          delete candidateDetails["sub_industry"];

          state.offer = { ...payload?.data };
          state.positionDetails = {
            ...positionDetails,
            company_sub_job_function_id:
              offer_position_detail?.company_sub_job_function?.id,
            country_id: offer_position_detail?.country?.id,
            region: offer_position_detail?.region,
            currency_id: offer_position_detail?.currency?.id,
          };
          state.candidateDetails = {
            ...candidateDetails,
            country_id: offer_candidate_detail?.country?.id,
            currency_id: offer_candidate_detail?.currency?.id,
            current_sub_job_function_id:
              offer_candidate_detail?.current_sub_job_function?.id,
            sub_industry_id: offer_candidate_detail?.sub_industry?.id,
          };

          state.allowance_data_fingerprint =
            payload?.data?.allowance_data_fingerprint ||
            payload?.data?.allowance_data_status?.saved_fingerprint ||
            payload?.data?.allowance_data_status?.current_fingerprint ||
            null;
        }
      )
      .addMatcher(
        offersApi.endpoints.fetchCurrencyRate.matchFulfilled,
        (state, action) => {
          state.currencyRate = action?.payload?.data?.result
            ? parseFloat(
                Object.values(action?.payload?.data?.result)[0]
              ).toFixed(5)
            : null;

          state.currentCandidateCurrency = action.payload?.data
            ?.candidate_currency?.result
            ? parseFloat(
                Object.values(
                  action.payload?.data?.candidate_currency?.result
                )[0]
              ).toFixed(5)
            : null;
        }
      )
      // error handling below
      .addMatcher(
        offersApi.endpoints.fetchCurrencyRate.matchRejected,
        (state, { payload }) => {
          customErrorMessage(payload?.message);
        }
      )

      .addMatcher(
        offersApi.endpoints.fetchStiCurrencyRate.matchFulfilled,
        (state, action) => {
          state.stiBonusCurrencyRate = action.payload?.data?.result
            ? parseFloat(
                Object.values(action.payload?.data?.result)[0]
              )?.toFixed(2)
            : null;
        }
      )

      .addMatcher(
        offersApi.endpoints.fetchOfferCurrencyRate.matchFulfilled,
        (state, action) => {
          state.offerCurrency = !action.payload?.data?.result
            ? null
            : (Object.values(action.payload?.data?.result)[0] as number | null);
        }
      )
      // .addMatcher(
      //   offersApi.endpoints.fetchAutoPopulateData.matchFulfilled,
      //   (state, action) => {

      //     const isManual = action.meta?.arg?.originalArgs?.manual;

      //     if (!isManual && !state.shouldAutoPopulate) {
      //       const { stock_data } = action.payload.data || {};
      //       state.stock_data = stock_data;
      //       return;
      //     }

      //     const { fixed_cash, lti, sti, stock_data, benefit_plan } =
      //       action.payload.data || {};

      //     let currency = state.offerCurrency;

      //     // ---------------- FIXED CASH ----------------
      //     const cash_allowances =
      //       fixed_cash?.map(
      //         ({
      //           allowance_name,
      //           id,
      //           value,
      //           is_percentage,
      //           is_basic_pay,
      //         }: any) => {
      //           const rawValue = Number(value || 0);

      //           const updatedValue =
      //             is_percentage
      //               ? calculatePercentageAmount(
      //                 Number(
      //                   state.offerModeller.offer_fixed_cash?.proposed_annual_base || 0
      //                 ),
      //                 rawValue
      //               )
      //               : rawValue;

      //           console.log("FIXED CASH CALCULATED", {
      //             rawValue,
      //             updatedValue,
      //             is_percentage,
      //           });

      //           return {
      //             is_current: false,
      //             auto_populate: true,
      //             value: updatedValue,
      //             original_value: rawValue,
      //             is_basic_pay,
      //             is_percentage,
      //             percentage: rawValue,
      //             name: allowance_name || "N/A",
      //             id,
      //           };
      //         }
      //       ) || [];

      //     console.log("CASH ALLOWANCES RESULT", cash_allowances);

      //     const fixedCashTotals = calcluateFixedTotal(
      //       {
      //         ...state.offerModeller.offer_fixed_cash,
      //         allowances: cash_allowances,
      //       },
      //       [
      //         "current_compa_ratio",
      //         "current_market_ratio",
      //         "proposed_compa_ratio",
      //         "proposed_market_ratio",
      //       ]
      //     );

      //     console.log("FIXED CASH TOTALS AFTER FIX", fixedCashTotals);

      //     // ---------------- STI ----------------
      //     // const bonuses =
      //     //   sti?.map(
      //     //     ({ plan, id, value, is_percentage, is_basic_pay }: any) => ({
      //     //       is_current: false,
      //     //       auto_populate: true,
      //     //       value: Number(value || 0),
      //     //       original_value: Number(value || 0),
      //     //       name: `${plan?.name} (${plan?.type?.name})` || "N/A",
      //     //       percentage: value,
      //     //       is_percentage,
      //     //       is_basic_pay,
      //     //       id,
      //     //     })
      //     //   ) || [];

      //     // const stiTotals = getTotal(
      //     //   {
      //     //     ...state.offerModeller.offer_sti, // ✅ FIXED
      //     //     bonuses,
      //     //     current_bonus_target:
      //     //       fixedCashTotals?.current_total &&
      //     //         state.offerModeller.offer_sti?.current_bonus_target
      //     //         ? round(
      //     //           (fixedCashTotals.current_total *
      //     //             state.offerModeller.offer_sti.current_bonus_target) /
      //     //           100
      //     //         )
      //     //         : null,
      //     //     proposed_bonus_target:
      //     //       fixedCashTotals?.proposed_total &&
      //     //         state.offerModeller.offer_sti?.proposed_bonus_target
      //     //         ? round(
      //     //           (fixedCashTotals.proposed_total *
      //     //             state.offerModeller.offer_sti.proposed_bonus_target) /
      //     //           100
      //     //         )
      //     //         : null,
      //     //   },
      //     //   []
      //     // );

      //     // ---------------- STI ----------------
      //     const bonuses =
      //       sti?.map(
      //         ({ plan, id, value, is_percentage, is_basic_pay }: any) => {
      //           const rawValue = Number(value || 0);

      //           const calculatedValue = is_percentage
      //             ? is_basic_pay
      //               ? calculatePercentageAmount(
      //                 Number(
      //                   state.offerModeller.offer_fixed_cash?.proposed_annual_base || 0
      //                 ),
      //                 rawValue
      //               )
      //               : calculatePercentageAmount(
      //                 Number(fixedCashTotals?.proposed_total || 0),
      //                 rawValue
      //               )
      //             : rawValue;

      //           return {
      //             is_current: false,
      //             auto_populate: true,

      //             value: calculatedValue,
      //             original_value: rawValue,
      //             percentage: rawValue,

      //             name: `${plan?.name} (${plan?.type?.name})` || "N/A",
      //             is_percentage,
      //             is_basic_pay,
      //             id,
      //           };
      //         }
      //       ) || [];

      //     const stiTotals = calcluateStiTotal(
      //       {
      //         ...state.offerModeller.offer_sti,

      //         bonuses,

      //         current_bonus_target:
      //           fixedCashTotals?.current_total &&
      //             state.offerModeller.offer_sti?.current_bonus_target &&
      //             !state.offerModeller.offer_sti?.is_current_sale_incentive
      //             ? round(
      //               (fixedCashTotals.current_total *
      //                 state.offerModeller.offer_sti.current_bonus_target) /
      //               100
      //             )
      //             : 0,

      //         proposed_bonus_target:
      //           state.offerModeller.offer_fixed_cash?.proposed_annual_base &&
      //             state.offerModeller.offer_sti?.proposed_bonus_target
      //             ? round(
      //               (state.offerModeller.offer_fixed_cash.proposed_annual_base *
      //                 state.offerModeller.offer_sti.proposed_bonus_target) /
      //               100
      //             )
      //             : null,

      //         fixed_proposed_total: fixedCashTotals?.proposed_total,

      //         proposed_annual_base:
      //           state.offerModeller.offer_fixed_cash?.proposed_annual_base,
      //       },
      //       []
      //     );

      //     console.log("STI TOTALS", stiTotals);

      //     // ---------------- LTI ----------------
      //     const ltis =
      //       lti?.map(({ plan, id, equity_mid }: any) => ({
      //         is_current: false,
      //         auto_populate: true,
      //         value: Number(equity_mid || 0),
      //         equity_mid: Number(equity_mid || 0),
      //         is_amount: plan?.is_amount,
      //         unit: false,
      //         name: `${plan?.name} (${plan?.type?.name})` || "N/A",
      //         id,
      //       })) || [];

      //     const ltiTotals = getTotalLti({
      //       ...state.offerModeller.offer_lti, // ✅ FIXED
      //       other_ltis: ltis,
      //     });

      //     // ---------------- BENEFITS ----------------
      //     // const bonuses_benefit_plan =
      //     //   benefit_plan?.map(({ name, id, is_percentage, detail }: any) => {
      //     //     let updatedValue = Number(detail?.value || 0);
      //     //     console.log(benefit_plan)

      //     //     return {
      //     //       is_current: false,
      //     //       auto_populate: true,
      //     //       value: updatedValue,
      //     //       original_value: updatedValue,
      //     //       name: name || "N/A",
      //     //       percentage: detail?.value,
      //     //       calculation_basis: detail?.calculation_basis,
      //     //       id,
      //     //     };
      //     //   }) || [];

      //     const bonuses_benefit_plan =
      //       benefit_plan
      //         ?.filter(({ exclude_from_offer }: any) => !exclude_from_offer)
      //         ?.map(
      //           ({
      //             id,
      //             name,
      //             calculation_basis,
      //             benefit_value_or_cte,
      //             benefit_limit,
      //             benefit_value_or_limit,
      //             benefit_entitlement,
      //             show_custom_detail,
      //             custom_detail,
      //             policy_manual_url,
      //             category,
      //             exclude_from_offer,
      //           }: any) => {
      //             const benefitValue = Number(benefit_value_or_cte || 0);

      //             return {
      //               id,
      //               name: name || "N/A",

      //               is_current: false,
      //               auto_populate: true,

      //               value: benefitValue,
      //               original_value: benefitValue,
      //               percentage: benefitValue,

      //               calculation_basis: calculation_basis || "flat",
      //               benefit_limit: Number(benefit_limit || 0),
      //               benefit_value_or_limit,
      //               benefit_entitlement,

      //               show_custom_detail: Boolean(show_custom_detail),
      //               custom_detail: custom_detail || null,

      //               policy_manual_url,
      //               category,
      //               exclude_from_offer: Boolean(exclude_from_offer),
      //             };
      //           }
      //         ) || [];

      //     console.log(
      //       "BENEFITS AFTER MAP",
      //       bonuses_benefit_plan
      //     );

      //     // ---------------- UPDATE STATE ----------------
      //     state.stock_data = stock_data;

      //     state.offerModeller = {
      //       ...state.offerModeller,
      //       offer_fixed_cash: {
      //         ...state.offerModeller.offer_fixed_cash,
      //         current_total: fixedCashTotals.current_total,
      //         proposed_total: fixedCashTotals.proposed_total,
      //         allowances: cash_allowances,
      //       },
      //       offer_sti: {
      //         ...state.offerModeller.offer_sti,
      //         current_total_bonus: stiTotals.current_total,
      //         proposed_total_bonus: stiTotals.proposed_total,
      //         bonuses,
      //       },
      //       offer_lti: {
      //         ...state.offerModeller.offer_lti,
      //         current_total_lti: ltiTotals.current_total,
      //         proposed_total_lti: ltiTotals.proposed_total,
      //         other_ltis: ltis,
      //       },
      //       offer_benefits: {
      //         ...state.offerModeller.offer_benefits,
      //         bonuses: bonuses_benefit_plan,
      //       },
      //     };

      //     console.log(
      //       "FINAL FIXED CASH",
      //       state.offerModeller.offer_fixed_cash
      //     );

      //     console.log(
      //       "FINAL STI",
      //       state.offerModeller.offer_sti
      //     );

      //     console.log(
      //       "FINAL BENEFITS",
      //       state.offerModeller.offer_benefits
      //     );

      //     state.shouldAutoPopulate = false;

      //     state.allowance_data_fingerprint =
      //       action.payload?.data?.allowance_data_fingerprint || null;
      //   }
      // );

      .addMatcher(
        offersApi.endpoints.fetchAutoPopulateData.matchFulfilled,
        (state, action) => {
          const isManual = action.meta?.arg?.originalArgs?.manual;

          // Normal/background fetch should NOT replace the saved offer snapshot.
          if (!isManual && !state.shouldAutoPopulate) {
            const { stock_data } = action.payload.data || {};
            state.stock_data = stock_data;
            return;
          }

          const { fixed_cash, lti, sti, stock_data, benefit_plan } =
            action.payload.data || {};

          /*
           * Preserve every current-side row, plus any row the fresh Fetch Latest
           * payload does not supply (rows added by hand through "Add more").
           *
           * Rows are matched on rowKey (name + is_current) rather than on marker
           * properties. `auto_populate` and `readonly` only live for the length of
           * a browser session - the API persists neither - so after save + reload a
           * hand-added row carries no marker at all. Filtering on the markers
           * dropped those rows here, and because they are not company rows the
           * payload never brought them back, so they disappeared from the offer.
           *
           * Keying on the payload also avoids the duplication this filter
           * originally guarded against: a saved system row IS supplied by the
           * payload, so it is dropped here and re-added from the fresh list (with
           * any user edits restored by restoreUserEdits).
           */
          const preserveManualAndCurrent = (
            items: any[] = [],
            freshRows: any[] = []
          ) => {
            const freshKeys = new Set((freshRows || []).map(rowKey));

            return items.filter(
              (item: any) =>
                item?.is_current === true ||
                (item?.auto_populate !== true && !freshKeys.has(rowKey(item)))
            );
          };

          // ============================================================
          // FIXED CASH
          // ============================================================

          const cash_allowances =
            fixed_cash?.map(
              ({
                allowance_name,
                id,
                value,
                is_percentage,
                is_basic_pay,
              }: any) => {
                const rawValue = Number(value || 0);

                const updatedValue = is_percentage
                  ? calculatePercentageAmount(
                      Number(
                        state.offerModeller.offer_fixed_cash
                          ?.proposed_annual_base || 0
                      ),
                      rawValue
                    )
                  : rawValue;

                console.log("FIXED CASH CALCULATED", {
                  rawValue,
                  updatedValue,
                  is_percentage,
                });

                return {
                  is_current: false,
                  auto_populate: true,
                  value: updatedValue,
                  original_value: rawValue,
                  is_basic_pay,
                  is_percentage,
                  percentage: rawValue,
                  name: allowance_name || "N/A",
                  id,
                };
              }
            ) || [];

          const preservedFixedCashAllowances = preserveManualAndCurrent(
            state.offerModeller.offer_fixed_cash?.allowances || [],
            cash_allowances
          );

          const mergedCashAllowances = [
            ...restoreUserEdits(
              state.offerModeller.offer_fixed_cash?.allowances,
              cash_allowances,
              "offer_fixed_cash"
            ),
            ...preservedFixedCashAllowances,
          ];

          const fixedCashTotals = calcluateFixedTotal(
            {
              ...state.offerModeller.offer_fixed_cash,
              allowances: mergedCashAllowances,
            },
            [
              "current_compa_ratio",
              "current_market_ratio",
              "proposed_compa_ratio",
              "proposed_market_ratio",
            ]
          );

          console.log("FIXED CASH TOTALS AFTER FIX", fixedCashTotals);

          // ============================================================
          // STI
          // ============================================================

          const bonuses =
            sti?.map(
              ({ plan, id, value, is_percentage, is_basic_pay }: any) => {
                const rawValue = Number(value || 0);

                const calculatedValue = is_percentage
                  ? is_basic_pay
                    ? calculatePercentageAmount(
                        Number(
                          state.offerModeller.offer_fixed_cash
                            ?.proposed_annual_base || 0
                        ),
                        rawValue
                      )
                    : calculatePercentageAmount(
                        Number(fixedCashTotals?.proposed_total || 0),
                        rawValue
                      )
                  : rawValue;

                return {
                  is_current: false,
                  auto_populate: true,

                  value: calculatedValue,
                  original_value: rawValue,
                  percentage: rawValue,

                  name: `${plan?.name} (${plan?.type?.name})` || "N/A",

                  is_percentage,
                  is_basic_pay,
                  id,
                };
              }
            ) || [];

          const preservedStiBonuses = preserveManualAndCurrent(
            state.offerModeller.offer_sti?.bonuses || [],
            bonuses
          );

          const mergedStiBonuses = [
            ...restoreUserEdits(
              state.offerModeller.offer_sti?.bonuses,
              bonuses,
              "offer_sti"
            ),
            ...preservedStiBonuses,
          ];

          const stiTotals = calcluateStiTotal(
            {
              ...state.offerModeller.offer_sti,

              bonuses: mergedStiBonuses,

              current_bonus_target:
                fixedCashTotals?.current_total &&
                state.offerModeller.offer_sti?.current_bonus_target &&
                !state.offerModeller.offer_sti?.is_current_sale_incentive
                  ? round(
                      (fixedCashTotals.current_total *
                        state.offerModeller.offer_sti.current_bonus_target) /
                        100
                    )
                  : 0,

              proposed_bonus_target:
                state.offerModeller.offer_fixed_cash?.proposed_annual_base &&
                state.offerModeller.offer_sti?.proposed_bonus_target
                  ? round(
                      (state.offerModeller.offer_fixed_cash
                        .proposed_annual_base *
                        state.offerModeller.offer_sti.proposed_bonus_target) /
                        100
                    )
                  : null,

              fixed_proposed_total: fixedCashTotals?.proposed_total,

              proposed_annual_base:
                state.offerModeller.offer_fixed_cash?.proposed_annual_base,
            },
            []
          );

          console.log("STI TOTALS", stiTotals);

          // ============================================================
          // LTI
          // ============================================================

          const ltis =
            lti?.map(({ plan, id, equity_mid }: any) => ({
              is_current: false,
              auto_populate: true,
              value: Number(equity_mid || 0),
              equity_mid: Number(equity_mid || 0),
              is_amount: plan?.is_amount,
              unit: false,
              name: `${plan?.name} (${plan?.type?.name})` || "N/A",
              id,
            })) || [];

          const preservedLtis = preserveManualAndCurrent(
            state.offerModeller.offer_lti?.other_ltis || [],
            ltis
          );

          const mergedLtis = [
            ...restoreUserEdits(
              state.offerModeller.offer_lti?.other_ltis,
              ltis,
              "offer_lti"
            ),
            ...preservedLtis,
          ];

          const ltiTotals = getTotalLti({
            ...state.offerModeller.offer_lti,
            other_ltis: mergedLtis,
          });

          // ============================================================
          // BENEFITS
          // ============================================================

          const bonuses_benefit_plan =
            benefit_plan
              ?.filter(({ exclude_from_offer }: any) => !exclude_from_offer)
              ?.map(
                ({
                  id,
                  name,
                  calculation_basis,
                  benefit_value_or_cte,
                  benefit_limit,
                  benefit_value_or_limit,
                  benefit_entitlement,
                  show_custom_detail,
                  custom_detail,
                  policy_manual_url,
                  category,
                  exclude_from_offer,
                }: any) => {
                  const benefitValue = Number(benefit_value_or_cte || 0);

                  return {
                    id,
                    name: name || "N/A",

                    is_current: false,
                    auto_populate: true,

                    value: benefitValue,
                    original_value: benefitValue,
                    percentage: benefitValue,

                    calculation_basis: calculation_basis || "flat",

                    benefit_limit: Number(benefit_limit || 0),

                    benefit_value_or_limit,
                    benefit_entitlement,

                    show_custom_detail: Boolean(show_custom_detail),

                    custom_detail: custom_detail || null,

                    policy_manual_url,
                    category,

                    exclude_from_offer: Boolean(exclude_from_offer),
                  };
                }
              ) || [];

          const preservedBenefits = preserveManualAndCurrent(
            state.offerModeller.offer_benefits?.bonuses || [],
            bonuses_benefit_plan
          );

          const mergedBenefits = [
            ...restoreUserEdits(
              state.offerModeller.offer_benefits?.bonuses,
              bonuses_benefit_plan,
              "offer_benefits"
            ),
            ...preservedBenefits,
          ];

          console.log("BENEFITS AFTER MAP", bonuses_benefit_plan);

          // ============================================================
          // UPDATE STATE
          // ============================================================

          state.stock_data = stock_data;

          state.offerModeller = {
            ...state.offerModeller,

            offer_fixed_cash: {
              ...state.offerModeller.offer_fixed_cash,
              current_total: fixedCashTotals.current_total,
              proposed_total: fixedCashTotals.proposed_total,
              allowances: mergedCashAllowances,
            },

            offer_sti: {
              ...state.offerModeller.offer_sti,
              current_total_bonus: stiTotals.current_total,
              proposed_total_bonus: stiTotals.proposed_total,
              bonuses: mergedStiBonuses,
            },

            offer_lti: {
              ...state.offerModeller.offer_lti,
              current_total_lti: ltiTotals.current_total,
              proposed_total_lti: ltiTotals.proposed_total,
              other_ltis: mergedLtis,
            },

            offer_benefits: {
              ...state.offerModeller.offer_benefits,
              bonuses: mergedBenefits,
            },
          };

          console.log("FINAL FIXED CASH", state.offerModeller.offer_fixed_cash);

          console.log("FINAL STI", state.offerModeller.offer_sti);

          console.log("FINAL BENEFITS", state.offerModeller.offer_benefits);

          state.shouldAutoPopulate = false;

          state.allowance_data_fingerprint =
            action.payload?.data?.allowance_data_fingerprint || null;
        }
      );
  },
});

export const {
  handlePositionDetails,
  resetOffers,
  handleCandidateDetails,
  handleStage,
  handleOfferModeller,
  removeCurrency,
  handleCurrency,
  handleHiddenStiBonuses,
  resetOfferModeller,
  convertOffer,
  toggleShowCurrent,
} = offersSlice.actions;

export default offersSlice.reducer;
